import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Highlight } from "@/components/landing/Highlight";
import { useLanguage } from "@/context/LanguageContext";

const WHATSAPP_URL = "https://wa.me/33660235665";
const CALENDLY_URL = "https://calendar.app.google/C866HmTsf8RckhiE9";
const HERO_IMAGE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_0614d46d-eee4-4b28-994f-37db8280bda0/artifacts/uuyxcj2b_bring-me-cash-branquela.png";

const wordReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFlag = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} id="hero" className="relative pt-32 sm:pt-40 pb-20 overflow-hidden bg-[#FAFAF7]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <p className="font-['Ubuntu'] text-xs uppercase tracking-[0.25em] font-semibold text-[#9B5DE5] mb-4" data-testid="hero-eyebrow">
            {t.hero.eyebrowLine1} · {t.hero.eyebrowLine2}
          </p>

          <h1 className="font-['Cutive'] text-4xl sm:text-5xl lg:text-6xl leading-[1.15] font-bold text-black" data-testid="hero-title">
            {t.hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-2">
                <motion.span custom={i} initial="hidden" animate="visible" variants={wordReveal} className="block">
                  {line.split(" ").map((word, wi) => {
                    const clean = word.replace(/[.,]/g, "");
                    const isHi = t.hero.highlightWords.some((h) => clean.toLowerCase() === h.toLowerCase());
                    return (
                      <span key={wi} className="mr-2 inline-block">
                        {isHi ? <Highlight>{word}</Highlight> : word}
                      </span>
                    );
                  })}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="font-['Ubuntu'] text-base sm:text-lg text-black/70 mt-6 max-w-md"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.6 }}
            className="flex flex-wrap gap-4 mt-8"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-hero"
              className="inline-flex items-center gap-2 bg-[#FFE440] text-black font-['Ubuntu'] font-bold rounded-full px-6 py-3.5 border-2 border-black shadow-[4px_4px_0px_#121212] hover:shadow-[2px_2px_0px_#121212] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            >
              {t.cta.whatsapp}
            </a>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-calendly-hero"
              className="inline-flex items-center gap-2 bg-white text-black font-['Ubuntu'] font-bold rounded-full px-6 py-3.5 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              {t.cta.schedule}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="flex flex-wrap gap-x-8 gap-y-3 mt-10"
          >
            {t.hero.badges.map((b, i) => (
              <div key={i} className="flex items-center gap-2 font-['Ubuntu'] text-sm text-black/70" data-testid={`hero-badge-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#00BBF9]" /> {b}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[320px] sm:h-[420px]">
          <motion.div
            style={{ y: yPhoto }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="absolute right-0 top-0 w-[78%] max-w-[380px] h-[320px] sm:h-[420px] rounded-[2rem] overflow-hidden border-4 border-black shadow-[10px_10px_0px_#FFE440]"
          >
            <img src={HERO_IMAGE} alt="Bring Me Cash" className="w-full h-full object-cover" data-testid="hero-photo" />
          </motion.div>

          <motion.div style={{ y: yFlag }} className="absolute left-0 top-6 sm:top-10 flex flex-col z-20">
            <span className="text-4xl sm:text-5xl" role="img" aria-label="brazil flag">🇧🇷</span>
            <span className="text-4xl sm:text-5xl -mt-3" role="img" aria-label="france flag">🇫🇷</span>
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, rotate: -20 }}
            animate={{ opacity: 1, rotate: -3 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="absolute -left-2 bottom-14 sm:bottom-16 bg-[#FFFBEB] border-2 border-black rounded-lg px-4 py-3 shadow-[4px_4px_0px_#121212] max-w-[190px] z-20 whitespace-pre-line"
          >
            <p className="font-['Ubuntu'] text-xs font-bold uppercase leading-snug">{t.hero.stickerTop}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, rotate: 20 }}
            animate={{ opacity: 1, rotate: 2 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="absolute right-2 bottom-2 bg-[#00F5D4] border-2 border-black rounded-lg px-4 py-3 shadow-[4px_4px_0px_#121212] max-w-[180px] z-20 whitespace-pre-line"
          >
            <p className="font-['Ubuntu'] text-xs font-bold leading-snug">{t.hero.stickerBottom}</p>
          </motion.div>

          <p
            className="absolute -bottom-9 left-0 sm:left-1/2 sm:-translate-x-1/2 font-['Cutive'] italic text-sm text-black/60 whitespace-nowrap"
            data-testid="hero-flag-line"
          >
            {t.hero.flagLine} ♡
          </p>
        </div>
      </div>
    </section>
  );
};
